import React, { useMemo, useRef } from 'react';
import { Form, Button, Input, Select, Row, Col, message } from 'antd';
import { useQuery, useMutation } from '@apollo/client';
import {
  CREATE_PARTNER,
  QUERY_LIST_REFERRERS,
} from '~pages/dashboard/referral/partner-account/queries';
import { availableCountries, partnerType } from '../common';

const { Option } = Select;

function Editor({ t, onClose, form: { getFieldDecorator, validateFields, setFieldsValue } }) {
  const formRef = useRef(null);
  const partnerTypesOpts = useMemo(() => {
    return Object.keys(partnerType).map(type => ({
      text: t(`cms.partner.partner_type.${type}`),
      value: partnerType[type],
    }));
  }, []);

  const { loading: referrerListLoading, data: referrerListResponse } = useQuery(
    QUERY_LIST_REFERRERS,
    {
      variables: {
        pageNumber: 1,
        pageSize: 100,
      },
    },
  );

  const [createPartner, { loading: createPartnerLoading }] = useMutation(CREATE_PARTNER, {
    onError: e => {
      let errMsg = null;
      e.graphQLErrors;

      if (e.message === '[object Object]') {
        let msgKey = Object.keys(e.graphQLErrors[0]?.message);
        if (msgKey) {
          errMsg = e.graphQLErrors[0]?.message[`${msgKey}`][0];
        }
      }
      message.error(errMsg || e.message);
    },
  });

  const referrerList = useMemo(() => {
    return (
      referrerListResponse?.referrers?.edges.map(edge => ({
        label: `${edge?.node?.firstName || ''} ${edge?.node?.lastName || ''}`,
        value: edge.node.id,
      })) || []
    );
  }, [referrerListResponse]);

  const handleSubmit = e => {
    e.preventDefault();
    validateFields(async (err, values) => {
      if (!err) {
        console.log('Received values of form: ', values);
        let params = {
          ...values,
        };
        if (!(!values.bankName && !values.beneficiaryName && !values.card)) {
          params = {
            ...params,
            partnerBanks: {
              bankName: values.bankName,
              beneficiaryName: values.beneficiaryName,
              card: values.card,
            },
          };
        }
        delete params.bankName;
        delete params.beneficiaryName;
        delete params.card;

        const response = await createPartner({
          variables: {
            input: {
              ...params,
            },
          },
        });
        if (response?.data?.createPartner) {
          message.success(
            t('cms.referral.partner.detail.editor.action.partner_status.toast.success'),
          );
          onClose && onClose();
        }
      } else {
        console.log('[ err ] >', err);
      }
    });
  };
  return (
    <>
      <div className="add-account-editor__main">
        <Form ref={formRef} layout="vertical">
          <h3 className="add-account-editor__section-title">Partner Info</h3>

          <Row gutter={24}>
            <Col span={12}>
              <Form.Item label="Account Name">
                {getFieldDecorator('name', {
                  initialValue: '',
                  rules: [
                    {
                      required: true,
                      message: 'Please input account name',
                    },
                  ],
                })(<Input placeholder="Account Name" />)}
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Partner Country">
                {getFieldDecorator('country', {
                  initialValue: undefined,
                  rules: [
                    {
                      required: true,
                      message: 'Please select partner country',
                    },
                  ],
                })(
                  <Select
                    onChange={v => {
                      setFieldsValue({
                        market: availableCountries.find(t => t.countryCode === v).market,
                      });
                    }}
                    placeholder="Select a country"
                  >
                    {availableCountries.map((c, i) => (
                      <Option key={i} value={c.countryCode}>
                        {c.name}
                      </Option>
                    ))}
                  </Select>,
                )}
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item label="Market">
                {getFieldDecorator('market', {
                  rules: [
                    {
                      required: true,
                      message: 'Please input market',
                    },
                  ],
                })(<Input disabled={true} placeholder="market" />)}
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Partner Type">
                {getFieldDecorator('type', {
                  rules: [
                    {
                      required: true,
                      message: 'Please select partner type',
                    },
                  ],
                })(
                  <Select placeholder="Select partner type">
                    {partnerTypesOpts.map((c, i) => (
                      <Option key={i} value={c.value}>
                        {c.text}
                      </Option>
                    ))}
                  </Select>,
                )}
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item label="Payment Preference">
                {getFieldDecorator('paymentPreference', {
                  initialValue: 'OFFLINE',
                  rules: [
                    {
                      required: true,
                      message: 'Please select status',
                    },
                  ],
                })(
                  <Select disabled={true} placeholder="Payment Preference">
                    {[
                      { label: 'Online', value: 'ONLINE' },
                      { label: 'Offline', value: 'OFFLINE' },
                    ].map((c, i) => (
                      <Option key={i} value={c.value}>
                        {c.label}
                      </Option>
                    ))}
                  </Select>,
                )}
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Partner Code">
                {getFieldDecorator('partnerCode', {
                  rules: [
                    {
                      required: true,
                      message: 'Please select partner code',
                    },
                    {
                      pattern: /^[A-Z]{3}-[0-9]{3}$/,
                      message:
                        'The code should follow the format of 3 English characters in capital letters + 3 numbers e.g. JJL-001',
                    },
                  ],
                })(<Input placeholder="Partner Code" />)}
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item label="Account Owner">
                {getFieldDecorator('referrerId', {
                  rules: [
                    {
                      required: true,
                      message: 'Please select Account Owner',
                    },
                  ],
                })(
                  <Select loading={referrerListLoading} placeholder="Select Account Owner">
                    {referrerList.map((c, i) => (
                      <Option key={i} value={c.value}>
                        {c.label}
                      </Option>
                    ))}
                  </Select>,
                )}
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Status">
                {getFieldDecorator('enabled', {
                  rules: [
                    {
                      required: true,
                      message: 'Please select status',
                    },
                  ],
                })(
                  <Select placeholder="Select a status">
                    {[true, false].map((c, i) => (
                      <Option key={i} value={c}>
                        {c ? 'Enabled' : 'Disabled'}
                      </Option>
                    ))}
                  </Select>,
                )}
              </Form.Item>
            </Col>
          </Row>

          <h3 className="add-account-editor__section-title">Partner Bank Account</h3>
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item label="Partner Bank Name">
                {getFieldDecorator('bankName', {})(<Input placeholder="Partner Bank Name" />)}
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Partner Beneficiary Name">
                {getFieldDecorator(
                  'beneficiaryName',
                  {},
                )(<Input placeholder="Partner Beneficiary Name" />)}
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item label="Partner Bank Account NO.">
                {getFieldDecorator('card', {})(<Input placeholder="Partner Bank Account NO." />)}
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </div>
      <div className="add-account-editor__footer">
        <Button size="large" className="add-account-editor__cancel-button" onClick={() => {}}>
          {t('cms.referral.partner.detail.editor.btn.cancel')}
        </Button>
        <Button
          type="primary"
          size="large"
          className="add-account-editor__save-button"
          loading={createPartnerLoading}
          htmlType="submit"
          onClick={handleSubmit}
        >
          {t('cms.referral.partner.detail.editor.btn.save')}
        </Button>
      </div>
    </>
  );
}

export default Form.create({ name: 'add-account' })(Editor);
